import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { BusinessArea, ListingSortOption, PropertyMarketingType } from '@ev-tech/search-modules-api';

import { type FeatureFlag } from '../types/searchModuleTypes';
import { SortingOptionId } from '../enums/enums';

export const PROPERTY_TYPE_PREFIX = 'search.searchbar.advFilter.propertyType.';
export const PROPERTY_SUBTYPE_PREFIX = 'search.searchbar.advFilter.propertySubType.';

const SORT_BY_PREFIX = 'search.searchbar.sortBy.';

export type SortOptionTranslationKey = Extract<SearchModuleTranslationKey, `search.searchbar.sortBy.${string}`>;

type SortOption = {
  id: SortingOptionId;
  label: SortOptionTranslationKey;
  sortingOption: ListingSortOption;
  tags: string[];
};

export const SORT_BY_OPTIONS: SortOption[] = [
  {
    id: SortingOptionId.SMART_SORT,
    label: `${SORT_BY_PREFIX}smartSort`,
    sortingOption: ListingSortOption.PREFERENCES,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.PREFERENCES,
    label: `${SORT_BY_PREFIX}preferences`,
    sortingOption: ListingSortOption.PREFERENCES,
    tags: [BusinessArea.residential, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.NEWEST_FIRST,
    label: `${SORT_BY_PREFIX}newestFirst`,
    sortingOption: ListingSortOption.PUBLISHED_AT_DESC,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.sale, PropertyMarketingType.rent],
  },
  {
    id: SortingOptionId.PRICE_ASC,
    label: `${SORT_BY_PREFIX}priceAsc`,
    sortingOption: ListingSortOption.SALES_PRICE_ASC,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.PRICE_DESC,
    label: `${SORT_BY_PREFIX}priceDesc`,
    sortingOption: ListingSortOption.SALES_PRICE_DESC,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.RENT_ASC,
    label: `${SORT_BY_PREFIX}rentAsc`,
    sortingOption: ListingSortOption.RENT_ASC,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.rent],
  },
  {
    id: SortingOptionId.RENT_DESC,
    label: `${SORT_BY_PREFIX}rentDesc`,
    sortingOption: ListingSortOption.RENT_DESC,
    tags: [BusinessArea.commercial, BusinessArea.residential, PropertyMarketingType.rent],
  },
  {
    id: SortingOptionId.LIVING_SURFACE_ASC,
    label: `${SORT_BY_PREFIX}livingSurfaceAsc`,
    sortingOption: ListingSortOption.LIVING_SURFACE_ASC,
    tags: [BusinessArea.residential, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.LIVING_SURFACE_DESC,
    label: `${SORT_BY_PREFIX}livingSurfaceDesc`,
    sortingOption: ListingSortOption.LIVING_SURFACE_DESC,
    tags: [BusinessArea.residential, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.TOTAL_SURFACE_ASC,
    label: `${SORT_BY_PREFIX}totalSurfaceAsc`,
    sortingOption: ListingSortOption.TOTAL_SURFACE_ASC,
    tags: [BusinessArea.commercial, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
  {
    id: SortingOptionId.TOTAL_SURFACE_DESC,
    label: `${SORT_BY_PREFIX}totalSurfaceDesc`,
    sortingOption: ListingSortOption.TOTAL_SURFACE_DESC,
    tags: [BusinessArea.commercial, PropertyMarketingType.rent, PropertyMarketingType.sale],
  },
] as const;

export const NEW_SEARCH_URL_MAP = {
  dev: 'https://new-search.envdevel.com/api/',
  stage: 'https://new-search.envstage.com/api/',
  prod: 'https://new-search.engelvoelkers.com/api/',
} as const;

export const GLOBAL_NETWORK_IMAGE_URL = 'https://uploadcare.engelvoelkers.com/516800bc-67a0-4216-bd24-0ee1293fad2b/';

export const BLURRY_FALLBACK_IMAGE_URL = 'https://uploadcare.engelvoelkers.com/bd2a765a-d840-42d3-bc25-b9ebff4da7f4/';

export const SKY_IMAGE_URL = 'https://uploadcare.engelvoelkers.com/818e736c-23b2-4060-b172-d0717831e922/';

export const WORK_EDITION_IMAGE_URL = 'https://uploadcare.engelvoelkers.com/e8a8156f-e859-4176-a594-9e1be4ff3a26/';

export const DIDOMI_VENDOR = {
  MAPTILER: 'maptilera-KZWnDQD7',
} as const;
export type DIDOMI_VENDOR = (typeof DIDOMI_VENDOR)[keyof typeof DIDOMI_VENDOR];

export const IMPRINT_URL_DE = 'https://www.engelvoelkers.com/de/de/impressum';
export const IMPRINT_URL_GLOBAL = 'https://www.engelvoelkers.com/de/en/imprint';

export const PENDING_USER_ACCOUNT_ITEM_KEY = 'pendingUserAccountItemToSave';

export const FeatureFlags = {
  IS_AI_SEARCH_ENABLED: 'isAiSearchEnabled',
  IS_MAP_ENABLED: 'isMapEnabled',
  IS_SMART_SORT_ENABLED: 'isSmartSortEnabled',
} as const;

export const DEFAULT_FEATURE_FLAGS: Record<FeatureFlag, boolean> = {
  [FeatureFlags.IS_AI_SEARCH_ENABLED]: false,
  [FeatureFlags.IS_MAP_ENABLED]: true,
  [FeatureFlags.IS_SMART_SORT_ENABLED]: false,
};
export const TRACKING_SEARCH_CONTEXT_CATEGORY = {
  ADVISOR: 'advisor',
} as const;

export const MAP_VIEW_RETURN_SESSION_KEY = 'ns-return-to-map-view';

export const PRIVATE_OFFICE_TILE_IMAGE_URL = 'https://uploadcare.engelvoelkers.com/de54c7df-ff77-4353-8456-28036e8ded5d/';
